import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import HeaderBack from "@/components/dashboard/Header/HeaderBack";
import MotionText from '@/components/common/Motion/MotionText';
import MotionDiv from '@/components/common/Motion/MotionDiv';

import {
  PiClipboard,
  PiPercent,
  PiChartLineUp,
  PiNotepad,
  PiListChecks,
  PiDevices,
  PiCalculator,
} from 'react-icons/pi';

import Button from '@/components/common/Button/Button';
import { usePageSetup } from '@/hooks/usePageSetup';

export const ExpensesPercentagePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  usePageSetup(t('expenses.pageTitle.percentageInfo'))

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack />
      <div className="flex flex-col px-5 pb-[7rem] gap-6">
        <MotionText
          as="h1"
          className="text-[2.5rem] font-hubot font-black uppercase leading-none"
        >
          {t('expenses.percentageInfo.title')}
        </MotionText>

        <MotionText
          as='p'
          className='text-sm text-zinc-700'
        >
          {t('expenses.percentageInfo.description')}
        </MotionText>

        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.percentageInfo.vehicle')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-1'>
          <MemoItem
            icon={<PiClipboard className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.vehicleRule1')} />
          <MemoItem
            icon={<PiPercent className="h-6 w-6 text-zinc-900" />}

            title={t('expenses.percentageInfo.vehicleRule2')} />
          <MemoItem
            icon={<PiPercent className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.vehicleRule3')} />
        </MotionDiv>

        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.percentageInfo.fuelSection')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-1'>
          <MemoItem
            icon={<PiChartLineUp className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.fuelRule1')} />
          <MemoItem
            icon={<PiNotepad className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.fuelRule2')} />
          <MemoItem
            icon={<PiListChecks className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.fuelRule3')} />
        </MotionDiv>

        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.percentageInfo.communication')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-1'>
          <MemoItem
            icon={<PiDevices className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.communicationRule1')} />
          <MemoItem
            icon={<PiPercent className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.communicationRule2')} />
        </MotionDiv>

        <MotionText
          as='h2'
          className='font-semibold text-sm text-base-black'
        >
          {t('expenses.percentageInfo.shortTermAssets')}
        </MotionText>

        <MotionDiv className='flex flex-col gap-6 mb-3'>
          <MemoItem
            icon={<PiCalculator className="h-6 w-6 text-zinc-900" />}
            title={t('expenses.percentageInfo.shortTermAssetsRule')} />
        </MotionDiv>

        <motion.div
          className="absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: 0.2,
            ease: "easeOut"
          }}
          whileTap={{ scale: 0.98 }}
        >
          <Button onClick={() => navigate(-1)}>
            <span>{t('common.ok')}</span>
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

const MemoItem = ({ icon, title }: { icon?: React.ReactNode; title: string }) => {
  return <MotionDiv className='flex flex-row items-center'>
    {icon && <div
      className="flex items-center justify-center min-w-12 min-h-12 rounded-full bg-zinc-100 mr-5">
      {icon}
    </div>
    }
    <div className='flex flex-col'>
      <p className='text-sm text-zinc-700'>{title}</p>
    </div>
  </MotionDiv>
}

export default ExpensesPercentagePage;
